import React, { useEffect } from 'react';
import { PercentInput } from '../percent-input/PercentInput';
import { CheckoutStatus } from '@app/react-common/models/CheckoutStatus';
import { CheckoutStatusDots } from '../checkout-status/CheckoutStatusDots';
import { ConfirmationModal } from '../confirmation-modal/ConfirmationModal';

export interface TrafficAllocationVariant {
  internalId?: number;
  name: string;
  checkoutStatus: CheckoutStatus;
  description: string;
  percentageTraffic: number;
  isSelected: boolean;
  widgetExternalId: number;
  widgetApiId: number;
  apiId: number;
}

export interface TrafficAllocationConfig {
  variants: TrafficAllocationVariant[];
}

export interface TrafficAllocationInputProps {
  trafficAllocationConfig: TrafficAllocationConfig;
  onChange: (valid: boolean, variants: TrafficAllocationVariant[]) => void;
  viewMode?: boolean;
  showValidation?: boolean;
  disablePercentageEdit?: boolean;
  hidePercentage?: boolean;
  maxTestGroups: number;
}

export interface TrafficAllocationFormData {
  variants: TrafficAllocationVariant[];
}

export const TrafficAllocationInputV2: React.FC<TrafficAllocationInputProps> = ({
  trafficAllocationConfig,
  onChange,
  viewMode = false,
  showValidation,
  disablePercentageEdit = false,
  hidePercentage = false,
  maxTestGroups,
}) => {
  const [validationMessageForModal, setValidationMessageForModal] = React.useState<string>('');
  const [formData, setFormData] = React.useState<TrafficAllocationFormData>({
    variants: trafficAllocationConfig.variants.map((variant, idx) => {
      return {
        ...variant,
        internalId: idx,
      };
    }),
  });

  useEffect(() => {
    setFormData((prevFormData) => {
      const propIds = new Set(trafficAllocationConfig.variants.map((v) => v.widgetExternalId));
      const prevIds = new Set(prevFormData.variants.map((v) => v.widgetExternalId));

      const needsSync =
        propIds.size !== prevIds.size ||
        Array.from(propIds).some((id) => !prevIds.has(id)) ||
        Array.from(prevIds).some((id) => !propIds.has(id));

      if (needsSync || prevFormData.variants.length === 0) {
        return {
          variants: trafficAllocationConfig.variants.map((variant, idx) => {
            const existing = prevFormData.variants.find((v) => v.widgetExternalId === variant.widgetExternalId);
            return {
              ...variant,
              internalId: existing?.internalId ?? idx,
            };
          }),
        };
      }

      const propsDiffer = prevFormData.variants.some((prevVariant) => {
        const propVariant = trafficAllocationConfig.variants.find(
          (v) => v.widgetExternalId === prevVariant.widgetExternalId,
        );
        if (!propVariant) return true;
        return (
          prevVariant.isSelected !== propVariant.isSelected ||
          prevVariant.percentageTraffic !== propVariant.percentageTraffic
        );
      });

      if (propsDiffer) {
        return {
          variants: prevFormData.variants.map((prevVariant) => {
            const propVariant = trafficAllocationConfig.variants.find(
              (v) => v.widgetExternalId === prevVariant.widgetExternalId,
            );
            if (propVariant) {
              return {
                ...propVariant,
                internalId: prevVariant.internalId,
              };
            }
            return prevVariant;
          }),
        };
      }

      return prevFormData;
    });
  }, [trafficAllocationConfig.variants]);

  useEffect(() => {
    const percentageSum = formData.variants
      .filter((x) => x.isSelected)
      .reduce((total, obj) => {
        return total + obj.percentageTraffic;
      }, 0);

    const isValid = percentageSum === 100;
    onChange(isValid, formData.variants);
  }, [formData]);

  const setVariantPercentage = (id: number, percentage: number) => {
    setFormData((oldFormData) => {
      return {
        variants: oldFormData.variants.map((variant) => {
          if (variant.internalId === id) {
            return {
              ...variant,
              percentageTraffic: percentage,
            };
          }
          return variant;
        }),
      };
    });
  };

  const toggleSelected = (id: number) => {
    setFormData((prevData) => {
      const clickedVariant = prevData.variants.find((v) => v.internalId === id);
      if (!clickedVariant) return prevData;

      const currentSelectedCount = prevData.variants.filter((v) => v.isSelected).length;
      if (!clickedVariant.isSelected && currentSelectedCount >= maxTestGroups) {
        setValidationMessageForModal(`Currently we only support up to ${maxTestGroups} variants for testing.`);
        return prevData;
      }

      const newSelectedCount = clickedVariant.isSelected ? currentSelectedCount - 1 : currentSelectedCount + 1;
      const percentage = newSelectedCount > 0 ? Math.floor(100 / (newSelectedCount + 1)) : 0;

      return {
        variants: prevData.variants.map((variant) => {
          if (variant.internalId === id) {
            return {
              ...variant,
              isSelected: !variant.isSelected,
              percentageTraffic: variant.isSelected ? 0 : percentage,
            };
          }
          if (variant.isSelected) {
            return { ...variant, percentageTraffic: percentage };
          }
          return variant;
        }),
      };
    });
  };

  return (
    <div className="traffic-allocation-input">
      {formData.variants.map((variant) => {
        return (
          <div
            key={variant.internalId}
            className={`traffic-allocation-input__item ${!viewMode && 'traffic-allocation-input__clickable'} ${variant.isSelected && 'traffic-allocation-input__item__selected'}`}
            onMouseDown={() => {
              if (!viewMode) {
                toggleSelected(variant.internalId);
              }
            }}>
            <div>
              <input
                className="traffic-allocation-input__item__checkbox"
                type="checkbox"
                checked={variant.isSelected}
                onChange={() => {}}
                disabled={viewMode}
              />
            </div>
            <div className="traffic-allocation-input__item__info">
              <div className={`traffic-allocation-input__item__info__title ${viewMode && 'disabled'}`}>
                {variant.name}
              </div>
              <div className={`traffic-allocation-input__item__info__description ${viewMode && 'disabled'}`}>
                {variant.description}
              </div>
            </div>
            <div className="traffic-allocation-input__item__status">
              <CheckoutStatusDots status={variant.checkoutStatus} ignorePadding={true} />
            </div>
            {!hidePercentage && variant.isSelected && (
              <div className="traffic-allocation-input__item__percentage">
                <PercentInput
                  value={variant.percentageTraffic}
                  setValue={(value) => {
                    if (!viewMode && !disablePercentageEdit) setVariantPercentage(variant.internalId, value);
                  }}
                  disabled={viewMode || disablePercentageEdit}
                  setValid={() => {}}
                  viewMode={viewMode}
                />
              </div>
            )}
          </div>
        );
      })}
      {validationMessageForModal && (
        <ConfirmationModal
          title={validationMessageForModal}
          buttonText={'Ok'}
          isOpen={validationMessageForModal.length > 0}
          mainContent={'Please unselect one of your variants to select this one.'}
          onClose={() => {
            setValidationMessageForModal('');
          }}
          onSuccess={() => {
            setValidationMessageForModal('');
          }}
          hideCancel={true}
        />
      )}
    </div>
  );
};
